import { ChecklistOutcome, colors, enums, formatters, type parsers } from "@fraction/shared";
import { memo, useMemo } from "react";
import Chip, { Variant } from "src/components/Chip";
import Tooltip from "src/components/Tooltip";
import { LightText, RegularText, SemiBoldText } from "src/components/v1";
import { AlertI } from "src/icons";

const STYLES = {
  header: "flex gap-4 text-sm items-center",
  chip: "ml-auto w-min",
  outcomeChip: "ml-auto w-fit",
  description: "text-xs mt-3 bg-gray-200 text-gray-700 py-2 px-3 rounded-lg",
  list: "list-none mt-4 mx-0 mb-0 p-0",
  listItem: "mb-[10px]",
  tooltipRow: "mb-1",
  tooltipContainer: "flex items-center text-xs gap-2",
  tooltipText:
    "text-left max-w-max w-[90%] bg-white text-gray-700 border border-solid border-gray-600 shadow-[0px_5px_10px_rgba(0,0,0,0.05)]",
  listItemText: "text-gray-700",
  smallListItemText: "text-gray-700 text-[10px]",
  taskChip: "min-w-[100px] mr-[6px]",
  toolTipIcon: "w-[10px] h-[10px] ml-[6px]",
};

const OUTCOMES: Record<string, { message: string; chipVariant: Variant }> = {
  [ChecklistOutcome.ACTION_REQUIRED]: {
    message: "Action required",
    chipVariant: Variant.YELLOW,
  },
  [ChecklistOutcome.FAIL]: {
    message: "Fail",
    chipVariant: Variant.RED,
  },
  [ChecklistOutcome.PENDING]: {
    message: "Pending",
    chipVariant: Variant.BASE,
  },
  [ChecklistOutcome.SUCCESS]: {
    message: "Success",
    chipVariant: Variant.GREEN,
  },
  [ChecklistOutcome.NOT_APPLICABLE]: {
    message: "N/A",
    chipVariant: Variant.GREEN,
  },
};

const TASK_STATUSES: Record<enums.ApplicationTaskStatus, { message: string; chipVariant: Variant }> = {
  [enums.ApplicationTaskStatus.TODO]: {
    message: "To Do",
    chipVariant: Variant.BASE,
  },
  [enums.ApplicationTaskStatus.IN_PROGRESS]: {
    message: "In Progress",
    chipVariant: Variant.YELLOW,
  },
  [enums.ApplicationTaskStatus.COMPLETE]: {
    message: "Complete",
    chipVariant: Variant.GREEN,
  },
  [enums.ApplicationTaskStatus.NOT_APPLICABLE]: {
    message: "N/A",
    chipVariant: Variant.GREEN,
  },
  [enums.ApplicationTaskStatus.FAILED]: {
    message: "Failed",
    chipVariant: Variant.RED,
  },
  [enums.ApplicationTaskStatus.TO_FIX]: {
    message: "To Fix",
    chipVariant: Variant.YELLOW,
  },
  [enums.ApplicationTaskStatus.DEPRECATED]: {
    message: "Deprecated",
    chipVariant: Variant.BASE,
  },
};

const getTaskStatus = (
  status: enums.ApplicationTaskStatus,
  required?: boolean
): { message: string; chipVariant: Variant } => {
  if (required === false && status !== enums.ApplicationTaskStatus.COMPLETE) {
    return TASK_STATUSES[enums.ApplicationTaskStatus.NOT_APPLICABLE];
  }
  return TASK_STATUSES[status];
};

const getTooltipText = (task: parsers.checklist.PerformedChecklist["tasks"][0]) => {
  if (!task.notes && !task.automatedNotes) {
    return null;
  }
  return (
    <RegularText>
      {task.notes ? (
        <div className={STYLES.tooltipRow}>
          <b>Notes:</b> {task.notes}
        </div>
      ) : null}
      {task.automatedNotes ? (
        <div className={STYLES.tooltipRow}>
          <b>Automation Notes:</b> {task.automatedNotes}
        </div>
      ) : null}
    </RegularText>
  );
};

const EmptyContainer = (props: any) => <div {...props} />;

const statusOrdering = [
  enums.ApplicationTaskStatus.FAILED,
  enums.ApplicationTaskStatus.TO_FIX,
  enums.ApplicationTaskStatus.IN_PROGRESS,
  enums.ApplicationTaskStatus.TODO,
  enums.ApplicationTaskStatus.COMPLETE,
  enums.ApplicationTaskStatus.NOT_APPLICABLE,
  enums.ApplicationTaskStatus.DEPRECATED,
];

const ChecklistDisplay = ({ checklist }: { checklist: parsers.checklist.PerformedChecklist }) => {
  const tasks = useMemo(
    () =>
      checklist.tasks
        .map((task) => ({
          ...task,
          outcome: getTaskStatus(task.status, task.required || undefined),
          tooltipText: getTooltipText(task),
          owner: task.user?.firstName
            ? formatters.user.userName(task.user)
            : task.taskOwners?.map(formatters.string.capitalize)?.join(", ") || "",
        }))
        .sort((a, b) => statusOrdering.indexOf(a.status) - statusOrdering.indexOf(b.status)),
    [checklist.tasks]
  );

  return (
    <div>
      <div className={STYLES.header}>
        <SemiBoldText>{checklist.definition.name}</SemiBoldText>
        <Chip className={STYLES.outcomeChip} variant={OUTCOMES[checklist.outcome].chipVariant}>
          {OUTCOMES[checklist.outcome].message}
        </Chip>
      </div>
      <RegularText className={STYLES.description}>{checklist.definition.description}</RegularText>
      <table className={STYLES.list}>
        {tasks.map((task) => {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const ContainerComponent = task.tooltipText ? Tooltip : EmptyContainer;
          return (
            <tr key={task.label} className={STYLES.listItem}>
              <td>
                <Chip className={STYLES.taskChip} variant={task.outcome.chipVariant}>
                  {task.outcome.message}
                </Chip>
              </td>
              <td>
                <ContainerComponent className={STYLES.tooltipContainer} text={task.tooltipText}>
                  <div>
                    <RegularText className={STYLES.listItemText}>
                      <div className="flex flex-row m-0">
                        {task.label}
                        {task.tooltipText ? (
                          <AlertI color={colors.palette.GREY_600} className={STYLES.toolTipIcon} />
                        ) : null}
                      </div>
                    </RegularText>
                    <LightText className={STYLES.smallListItemText}>{task.owner}</LightText>
                  </div>
                </ContainerComponent>
              </td>
            </tr>
          );
        })}
      </table>
    </div>
  );
};

export default memo(ChecklistDisplay);
